import type { FC } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Dialog, DialogContent, DialogTitle, Stack } from '@mui/material';

import { SECTION } from 'src/constants/section';
import type { ActionItemSelection } from 'src/types/myFollowUp';
import { useUserContext } from 'src/components/app/context/UserContext';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import ErrorSnackbar from 'src/components/common/Snackbar/ErrorSnackbar';
import SuccessSnackbar from 'src/components/common/Snackbar/SuccessSnackbar';
import useFollowUpDetail from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpDetailModal/FollowUpDetailContext';

import FollowUpDetailModalContent from './FollowUpDetailModalContent';
import FollowUpDetailModalHeader from './FollowUpDetailModalHeader';
import FollowUpDetailModalSidebar from './FollowUpDetailModalSidebar';

import { styles } from './index.styles';

interface FollowUpDetailModalProps {
  actionId: number | null;
  onClose: () => void;
  enable: boolean;
  onRowSelect: (selectedRow: ActionItemSelection) => void;
  useActionContext?: boolean;
}

const FollowUpDetailModal: FC<FollowUpDetailModalProps> = ({
  actionId,
  onClose,
  enable,
  onRowSelect,
  useActionContext = false,
}) => {
  const { t } = useTranslation();
  const {
    resetFormData,
    actionItem,
    loading,
    actionItemError,
    savedSuccessfully,
    deletedSuccessfully,
    deleteError,
    resetAfterDelete,
    onDelete,
    resetAfterSave,
  } = useFollowUpDetail();

  const onModalClose = () => {
    resetAfterSave();
    resetAfterDelete();
    resetFormData();
    onClose();
  };

  const {
    hasReadOnlyPermissionForModuleType,
    hasAdminPermissionForModuleType,
  } = useUserPermissions();
  const disabled =
    hasReadOnlyPermissionForModuleType(
      SECTION.FOLLOW_UP_ACTION,
      actionItem?.surveyId,
    ) && !hasAdminPermissionForModuleType(SECTION.FOLLOW_UP_ACTION);
  const { user } = useUserContext();
  const userIsOwner =
    user && actionItem && user.id === actionItem.followUpOwner;

  useEffect(() => {
    if (deletedSuccessfully) {
      const timer = setTimeout(() => {
        onModalClose();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [deletedSuccessfully]);

  return (
    <Dialog
      open={enable}
      onClose={onModalClose}
      maxWidth="md"
      fullWidth
      sx={styles.modalRoot}
    >
      <DialogTitle component="div" sx={styles.dialogTitle}>
        <FollowUpDetailModalHeader
          closeModal={onModalClose}
          onRowSelect={onRowSelect}
          actionId={actionId}
          useActionContext={useActionContext}
        />
      </DialogTitle>
      <DialogContent>
        {(actionId && !actionItem && !loading) || actionItemError ? (
          <Box sx={{ p: 4 }}>{t('unknownError')}</Box>
        ) : (
          <Stack direction="row" sx={{ width: '100%', height: '100%' }}>
            <FollowUpDetailModalContent
              closeModal={onModalClose}
              actionItem={actionItem}
              disabled={disabled}
            />
            <Box
              sx={{
                width: '30%',
              }}
            >
              <FollowUpDetailModalSidebar
                actionItem={actionItem}
                loading={loading}
                disabled={disabled}
                handleConfirmDelete={onDelete}
                userIsOwner={!!userIsOwner}
              />
            </Box>
          </Stack>
        )}
      </DialogContent>

      <SuccessSnackbar
        open={savedSuccessfully}
        onClose={resetAfterSave}
        title={t('followUp:comments.changesSaved')}
        message={t('followUp:comments.informationSaved')}
      />

      <SuccessSnackbar
        open={deletedSuccessfully}
        onClose={resetAfterDelete}
        title={t('myFollowUp:delete')}
        message={t('myFollowUp:deletedSuccessfully')}
      />

      <ErrorSnackbar
        open={!!deleteError}
        onClose={resetAfterDelete}
        title={t('myFollowUp:delete')}
        message={t('myFollowUp:deletionError')}
      />
    </Dialog>
  );
};

export default FollowUpDetailModal;
